import type { MeasureResult } from '@/plugins/tools/measure/MeasureData'
import type { ProfileResult } from '@/plugins/tools/profile/ProfileData'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type {
  AssetId,
  AssetKind,
  CameraMode,
  CameraState,
  ClipApplyMode,
  ClipDraftState,
  ClipSelectionMode,
  ControlMode,
  Disposable,
  PickResult,
} from '@/shared/types/core'
import type { InteractivePickInfo } from '@/shared/types/services'
import type {
  SpatialBindingChangedEventPayload,
  SpatialRepresentationErrorEventPayload,
  SpatialRepresentationEventPayload,
} from '@/shared/types/spatial'
import type {
  EngineFrameContext,
  ViewFrameContext,
  ViewId,
  ViewRect,
  ViewSnapshot,
} from '@/shared/types/view'

const loggerManager = getLoggerManager()
const log = loggerManager.getLogger('runtime')
const HANDLER_ERROR_LOG_INTERVAL_MS = 5_000
const HANDLER_ERROR_MESSAGE_MAX_LENGTH = 300

function formatErrorMessage(value: unknown): string {
  let text: string
  try {
    text = String(value)
  } catch {
    text = '<unprintable>'
  }
  const singleLine = text.replace(/\s*[\r\n]+\s*/g, ' ')
  return singleLine.length <= HANDLER_ERROR_MESSAGE_MAX_LENGTH
    ? singleLine
    : `${singleLine.slice(0, HANDLER_ERROR_MESSAGE_MAX_LENGTH - 3)}...`
}

/** 引擎事件类型映射表 */
export interface EngineEventMap {
  // === 引擎生命周期 ===
  /** 引擎完成初始化并进入可用状态 */
  // biome-ignore lint/suspicious/noConfusingVoidType: 事件映射中 void 表示无载荷
  'engine.ready': void
  /** 引擎已完全销毁，可安全释放外部引用 */
  // biome-ignore lint/suspicious/noConfusingVoidType: 事件映射中 void 表示无载荷
  'engine.disposed': void
  /** 引擎内部捕获到错误 */
  'engine.error': { error: Error; context?: string }

  // === 帧循环 ===
  /** 单帧开始，携带时间戳与帧间隔 */
  'frame.begin': { timestamp: number; delta: number }
  /** 单帧结束，携带引擎级帧上下文 */
  'frame.end': { engineFrame: EngineFrameContext }
  /** 视口帧开始 */
  'view.frame.begin': { viewId: ViewId; timestamp: number; delta: number }
  /** 视口帧结束 */
  'view.frame.end': { viewId: ViewId; frameContext?: ViewFrameContext }

  // === 相机 ===
  /** 主视口相机状态变化 */
  'camera.changed': { state: CameraState }
  /** 主视口相机模式切换 */
  'camera.modeChanged': { mode: CameraMode; previous: CameraMode }
  /** 指定视口相机状态变化 */
  'view.camera.changed': { viewId: ViewId; state: CameraState }
  /** 指定视口相机模式切换 */
  'view.camera.modeChanged': { viewId: ViewId; mode: CameraMode; previous: CameraMode }

  // === 控制器 ===
  /** 主视口控制模式切换 */
  'controls.modeChanged': { mode: ControlMode; previous: ControlMode }
  /** SpaceMouse 导航配置变更 */
  'controls.spacemouseNavigationChanged': {
    viewId: ViewId | null
    profile: 'mouse-only' | 'hybrid' | null
    rotateMode: 'arcball' | 'yaw-pitch'
    targetMode: 'bounds-center' | 'captured-point'
    rotationLocked: boolean
  }
  /** 主视口尺寸变化 */
  'viewport.resized': { width: number; height: number; pixelRatio: number }
  /** 指定视口控制模式切换 */
  'view.controls.modeChanged': { viewId: ViewId; mode: ControlMode; previous: ControlMode }
  /** 指定视口尺寸变化 */
  'view.viewport.resized': {
    viewId: ViewId
    rect: ViewRect
    pixelRatio?: number
  }

  // === View ===
  /** 视口创建完成 */
  'view.created': { viewId: ViewId; snapshot: ViewSnapshot }
  /** 视口移除完成 */
  'view.removed': { viewId: ViewId; previousActiveViewId: ViewId | null }
  /** 视口属性更新 */
  'view.updated': {
    viewId: ViewId
    snapshot: ViewSnapshot
    previous: ViewSnapshot
  }
  /** 激活视口切换 */
  'view.activated': {
    viewId: ViewId
    previousViewId: ViewId | null
    snapshot: ViewSnapshot
  }

  // === 资产生命周期 ===
  /** 资产开始加载 */
  'asset.loading': { id: AssetId; kind: AssetKind }
  /** 资产加载进度更新 */
  'asset.progress': {
    id?: AssetId
    kind?: AssetKind
    url?: string
    loaded: number
    total?: number
    progress?: number
  }
  /** 全部在途资产加载完成 */
  'asset.allLoaded': undefined
  /** 单项资产加载完成 */
  'asset.loaded': { id: AssetId; kind: AssetKind }
  /** 资产挂载到场景 */
  'asset.mounted': { id: AssetId; kind: AssetKind }
  /** 资产从场景卸载 */
  'asset.unmounted': { id: AssetId; kind: AssetKind }
  /** 资产资源释放 */
  'asset.disposed': { id: AssetId; kind: AssetKind }
  /** 资产可见性切换 */
  'asset.visibilityChanged': { id: AssetId; visible: boolean }
  /** 资产加载或运行时错误 */
  'asset.error': { id: AssetId; kind: AssetKind; error: Error }
  /** 资产导出开始 */
  'asset.exportStarted': { id: AssetId; kind: AssetKind; format: string }
  /** 资产导出完成 */
  'asset.exportCompleted': {
    id: AssetId
    kind: AssetKind
    format: string
    size?: number
  }
  /** 资产导出失败 */
  'asset.exportError': {
    id: AssetId
    kind: AssetKind
    format: string
    error: Error
  }

  // === 点云特有 ===
  /** 点云可见节点与点数更新 */
  'pointcloud.visibilityUpdated': {
    id: AssetId
    visibleNodes: number
    visiblePoints: number
  }

  // === 3DGS 特有 ===
  /** 3DGS LOD 层级更新 */
  'gsplat.lodUpdated': {
    id: AssetId
    loadedNodes: number
    totalPoints: number
  }

  // === CAD 特有 ===
  /** CAD 实体选中变化 */
  'cad.selectionChanged': { assetId: AssetId; entityIds: string[] }
  /** CAD 历史栈变化 */
  'cad.historyChanged': { assetId: AssetId; canUndo: boolean; canRedo: boolean }
  /** CAD 撤销或重做后状态广播 */
  'cad.undoRedo': { assetId: AssetId; canUndo: boolean; canRedo: boolean }
  /** CAD 当前工具切换 */
  'cad.toolChanged': {
    assetId: AssetId
    curToolName: string | symbol | null
    lastToolName: string | symbol | null
  }
  /** CAD 激活资产切换 */
  'cad.activeChanged': { assetId: AssetId | null; previousId: AssetId | null }
  /** CAD 图纸数据变化 */
  'cad.dataChanged': { assetId: AssetId }

  // === 选择 ===
  /** 全局选择集合变化 */
  'selection.changed': { selected: PickResult[]; previous: PickResult[] }

  // === 工具 ===
  /** 工具激活完成 */
  'tool.activated': { name: string; group: string }
  /** 工具停用完成 */
  'tool.deactivated': { name: string; group: string }
  /** 插件请求引擎停用自身（由 PotreeEngine 监听并调用 deactivateTool） */
  'tool.requestDeactivate': { name: string; group: string }
  /** 工具产出结果变更 */
  'tool.resultChanged':
    | { name: 'measure'; data: MeasureResult[] }
    | {
        name: 'clip'
        data:
          | ClipDraftState
          | {
              selectionMode: ClipSelectionMode
              clipMode: ClipApplyMode
              volumeCount: number
              canConfirm: boolean
            }
      }
    | { name: 'profile'; data: ProfileResult[] }
    | { name: string; data: unknown }

  // === 标注 ===
  /** 标注创建完成 */
  'annotation.created': { id: string }
  /** 标注属性更新 */
  'annotation.updated': { id: string }
  /** 标注被移除 */
  'annotation.removed': { id: string }
  /** 标注插入流程开始 */
  'annotation.insertionStarted': Record<string, never>
  /** 标注插入预览位置更新 */
  'annotation.insertionPreview': { position: import('three').Vector3 }
  /** 标注插入完成 */
  'annotation.insertionCompleted': Record<string, never>
  /** 标注插入被取消 */
  'annotation.insertionCancelled': Record<string, never>

  // === 交互拾取 ===
  /** 交互式点信息点击命中 */
  'interaction.pointInfo.click': InteractivePickInfo | null
  /** 交互式点信息悬停变化 */
  'interaction.pointInfo.move': InteractivePickInfo | null

  // === Spatial ===
  /** 空间表征构建完成 */
  'spatial.representationReady': SpatialRepresentationEventPayload
  /** 空间表征构建失败 */
  'spatial.representationError': SpatialRepresentationErrorEventPayload
  /** 空间绑定关系变化 */
  'spatial.bindingChanged': SpatialBindingChangedEventPayload
  /** 空间世界销毁完成 */
  'spatial.worldDisposed': { worldId: string }
}

/** 事件处理函数类型 */
type EventHandler<T> = (data: T) => void

/** EventBus 当前监听器与错误数量的只读诊断快照。 */
export interface EventBusDiagnostics {
  eventTypes: number
  listeners: number
  onceWrappers: number
  handlerErrors: number
  disposed: boolean
}

/**
 * 类型安全的事件总线。
 * - 编译期约束事件名和数据类型匹配
 * - handler 异常隔离：单个 handler 抛错不影响同事件的其他 handler
 */
export class EventBus implements Disposable {
  private listeners = new Map<string, Set<EventHandler<any>>>()
  /** once 注册的原始 handler 到 wrapper 的映射，支持 off 取消 */
  private onceWrappers = new Map<EventHandler<any>, EventHandler<any>>()
  /** 按 handler 和事件记录错误日志时间，避免高频事件持续异常时刷屏。 */
  private handlerErrorLogTimes = new WeakMap<EventHandler<any>, Map<string, number>>()
  private disposed = false
  /** emit 过程中捕获的 handler 错误（生产环境 console 被移除时仍可通过此属性获取） */
  handlerErrors: unknown[] = []

  /** 获取事件总线诊断计数，不暴露 handler 引用。 */
  getDiagnostics(): EventBusDiagnostics {
    let listeners = 0
    for (const handlers of this.listeners.values()) {
      listeners += handlers.size
    }
    return {
      eventTypes: this.listeners.size,
      listeners,
      onceWrappers: this.onceWrappers.size,
      handlerErrors: this.handlerErrors.length,
      disposed: this.disposed,
    }
  }

  /**
   * 注册事件监听器。
   *
   * @param event - 事件名，来自 EngineEventMap
   * @param handler - 事件回调
   */
  on<K extends keyof EngineEventMap>(event: K, handler: EventHandler<EngineEventMap[K]>): void {
    if (this.disposed) return
    let set = this.listeners.get(event as string)
    if (!set) {
      set = new Set()
      this.listeners.set(event as string, set)
    }
    set.add(handler)
  }

  /**
   * 注册一次性事件监听器，首次触发后自动注销。
   *
   * @param event - 事件名
   * @param handler - 事件回调
   */
  once<K extends keyof EngineEventMap>(event: K, handler: EventHandler<EngineEventMap[K]>): void {
    const wrapper: EventHandler<EngineEventMap[K]> = (data) => {
      this.onceWrappers.delete(handler)
      this.off(event, wrapper)
      handler(data)
    }
    this.onceWrappers.set(handler, wrapper)
    this.on(event, wrapper)
  }

  /**
   * 注销事件监听器，支持取消 once 注册的监听器。
   *
   * @param event - 事件名
   * @param handler - 先前通过 on 或 once 注册的原始 handler
   */
  off<K extends keyof EngineEventMap>(event: K, handler: EventHandler<EngineEventMap[K]>): void {
    const set = this.listeners.get(event as string)
    if (!set) return
    // 优先查找 once 包装器，若存在则删除包装器而非原始 handler
    const wrapper = this.onceWrappers.get(handler)
    if (wrapper) {
      set.delete(wrapper as EventHandler<any>)
      this.onceWrappers.delete(handler)
    } else {
      set.delete(handler)
    }
    if (set.size === 0) {
      this.listeners.delete(event as string)
    }
  }

  /**
   * 触发事件，异常隔离。
   *
   * @param event - 事件名
   * @param data - 事件数据，类型由 EngineEventMap 约束
   */
  emit<K extends keyof EngineEventMap>(
    event: K,
    ...args: EngineEventMap[K] extends void ? [data?: EngineEventMap[K]] : [data: EngineEventMap[K]]
  ): void {
    if (this.disposed) return
    const data = args[0] as EngineEventMap[K]
    const set = this.listeners.get(event as string)
    if (!set) return
    for (const handler of Array.from(set)) {
      try {
        handler(data)
      } catch (err) {
        this.handlerErrors.push(err)
        this.logHandlerError(event as string, handler, err)
      }
    }
  }

  private logHandlerError(event: string, handler: EventHandler<any>, err: unknown): void {
    try {
      if (!log.isEnabled(LogLevel.ERROR) || !this.shouldLogHandlerError(event, handler)) {
        return
      }
      const errorName = err instanceof Error ? err.name || 'Error' : 'NonErrorThrown'
      const errorMessage = err instanceof Error ? err.message : err
      log.error(
        `EventBus handler error event=${event}` +
          ` handler=${handler.name || '<anonymous>'}` +
          ` error=${formatErrorMessage(errorName)}` +
          ` message=${formatErrorMessage(errorMessage)}`
      )
    } catch {
      // 日志诊断失败不得破坏 handler 异常隔离，也不得阻断后续监听器。
    }
  }

  private shouldLogHandlerError(event: string, handler: EventHandler<any>): boolean {
    const now = loggerManager.now()
    let eventLogTimes = this.handlerErrorLogTimes.get(handler)
    if (eventLogTimes == null) {
      eventLogTimes = new Map<string, number>()
      this.handlerErrorLogTimes.set(handler, eventLogTimes)
    }
    const lastLoggedAt = eventLogTimes.get(event)
    if (lastLoggedAt !== undefined && now - lastLoggedAt < HANDLER_ERROR_LOG_INTERVAL_MS) {
      return false
    }
    eventLogTimes.set(event, now)
    return true
  }

  /**
   * 移除指定事件或全部事件的监听器。
   *
   * @param event - 目标事件名，未传入时清空全部监听器
   */
  removeAllListeners(event?: keyof EngineEventMap): void {
    if (event !== undefined) {
      const set = this.listeners.get(event as string)
      if (set) {
        // 清理该事件下所有 once wrapper 映射
        this.onceWrappers.forEach((wrapper, original) => {
          if (set!.has(wrapper as EventHandler<any>)) {
            this.onceWrappers.delete(original)
          }
        })
        this.listeners.delete(event as string)
      }
    } else {
      this.listeners.clear()
      this.onceWrappers.clear()
    }
  }

  /** 释放：清空所有监听器和错误记录 */
  dispose(): void {
    this.disposed = true
    this.listeners.clear()
    this.onceWrappers.clear()
    this.handlerErrors.length = 0
  }
}
